/*
 * RMU Ski Finder — "Find Your Skis!"
 *
 * Pure logic (CONFIG, scoreSki, recommendLength, nearestVariant, rankSkis) is
 * exported for node tests; the DOM layer only runs in a browser.
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) {
    module.exports = factory(root);
  } else {
    root.RMUSkiFinder = factory(root);
  }
})(typeof self !== 'undefined' ? self : this, function (root) {
  'use strict';

  /* ------------------------------------------------------------------
   * CONFIG — every weight, penalty and sizing number lives here.
   * RMU staff: review the sizing numbers against your own sizing guidance.
   * ------------------------------------------------------------------ */
  var CONFIG = {
    levels: ['beginner', 'intermediate', 'advanced', 'expert'],

    // target = what the answer asks for (1-5 scale); weight = how much it matters.
    scoring: {
      park: {
        targets: { all_day: 5, now_and_then: 3.5, not_really: 2 },
        weights: { all_day: 3, now_and_then: 2, not_really: 1 }
      },
      stiffness: {
        targets: { playful: 2, balanced: 3, powerful: 4.5 },
        weight: 3
      },
      powder: {
        targets: { rarely: 1.5, some_days: 3, every_chance: 4.5 },
        weight: 3
      },
      touring: {
        targets: { yes: 5, maybe: 3, lifts_only: null },
        weights: { yes: 2.5, maybe: 0.75, lifts_only: 0 }
      }
    },
    abilityMismatchPenalty: 12,   // user's level is not in the ski's abilities
    expertListsBeginnerPenalty: 2, // expert user + ski lists "beginner"
    sizeFarCm: 8,                  // nearest size is further than this from the recommendation...
    sizeFarPenalty: 6,             // ...add this penalty

    // Length recommendation (cm)
    length: {
      ability: { beginner: -12, intermediate: -6, advanced: 0, expert: 4 },
      feel: { playful: -2, balanced: 0, powerful: 2 },
      expectedKgPerM2: 22,   // expected weight (kg) = 22 x heightM^2
      heavyRatio: 1.15,      // above expected x 1.15
      heavyAdjust: 5,
      lightRatio: 0.85,      // below expected x 0.85
      lightAdjust: -5,
      showFitNoteOverCm: 4   // nearest size further than this -> "reach out" note
    },

    limits: { heightCm: [140, 210], weightLbs: [70, 330] },
    heightIn: [56, 80],      // 4'8" to 6'8" in the imperial picker
    autoAdvanceMs: 250,
    resultsCount: 3,
    bindingsUrl: '/collections/bindings'
  };

  var LB_PER_KG = 2.20462262;
  var CM_PER_IN = 2.54;

  /* ------------------------------------------------------------------
   * Question copy (RMU voice)
   * ------------------------------------------------------------------ */
  var QUESTIONS = [
    {
      key: 'level', title: "What's your skill level?",
      options: [
        { value: 'beginner', label: 'Beginner', desc: 'Linking turns on greens and blues' },
        { value: 'intermediate', label: 'Intermediate', desc: 'Comfortable on most blues, some blacks' },
        { value: 'advanced', label: 'Advanced', desc: 'Confident on blacks, starting to explore off-piste' },
        { value: 'expert', label: 'Expert', desc: 'Any terrain, any conditions' }
      ]
    },
    {
      key: 'park', title: 'Into tricks, side hits and park laps?',
      options: [
        { value: 'all_day', label: 'All day', desc: 'Switch landings, butters, park laps' },
        { value: 'now_and_then', label: 'Now and then', desc: 'Side hits and the odd jump' },
        { value: 'not_really', label: 'Not really', desc: "I'm here to ski the mountain" }
      ]
    },
    {
      key: 'feel', title: 'How do you want your skis to feel?',
      options: [
        { value: 'playful', label: 'Soft & playful', desc: 'Easy to bend, butter and throw around' },
        { value: 'balanced', label: 'Balanced', desc: 'A bit of both' },
        { value: 'powerful', label: 'Stiff & powerful', desc: 'Stable at speed, charges through chop' }
      ]
    },
    {
      key: 'pow', title: 'How much of your skiing is in powder?',
      options: [
        { value: 'rarely', label: 'Rarely', desc: 'Mostly groomers and hardpack' },
        { value: 'some_days', label: 'Some days', desc: 'Mixed resort conditions' },
        { value: 'every_chance', label: 'Every chance I get', desc: 'Storm days and deep snow' }
      ]
    },
    {
      key: 'tour', title: 'Will you be earning your turns?',
      options: [
        { value: 'yes', label: 'Yes, touring setup', desc: 'Mounting a pin or touring-capable binding' },
        { value: 'maybe', label: 'Maybe someday', desc: 'Want the option later' },
        { value: 'lifts_only', label: 'Lifts only', desc: 'Resort bindings for me' }
      ]
    }
  ];

  var RECAP = {
    level: { beginner: 'Beginner', intermediate: 'Intermediate', advanced: 'Advanced', expert: 'Expert' },
    feel: { playful: 'Playful', balanced: 'Balanced', powerful: 'Powerful' },
    pow: { rarely: 'Rare powder', some_days: 'Some powder', every_chance: 'Powder hound' },
    park: { all_day: 'Park all day', now_and_then: 'Some park', not_really: 'No park' },
    tour: { yes: 'Touring', maybe: 'Touring someday', lifts_only: 'Lifts only' }
  };

  // short hash codes
  var HASH_KEYS = { level: 'lvl', park: 'park', feel: 'feel', pow: 'pow', tour: 'tour' };
  var LEVEL_CODES = { beginner: 'beg', intermediate: 'int', advanced: 'adv', expert: 'exp' };

  var ANCHOR = 'find-your-planks';

  /* ------------------------------------------------------------------
   * Pure logic
   * ------------------------------------------------------------------ */
  function heightM(cm) { return cm / 100; }

  // Recommended length in cm (rounded) for a profile and, optionally, a specific ski.
  function recommendLength(profile, ski) {
    var L = CONFIG.length;
    var cm = profile.heightCm;
    var base = cm + (L.ability[profile.level] || 0) + (L.feel[profile.feel] || 0);
    var expectedKg = L.expectedKgPerM2 * heightM(cm) * heightM(cm);
    if (profile.weightKg > expectedKg * L.heavyRatio) base += L.heavyAdjust;
    else if (profile.weightKg < expectedKg * L.lightRatio) base += L.lightAdjust;
    if (ski && ski.lengthBias) base += Number(ski.lengthBias) || 0;
    return Math.round(base);
  }

  function variantLength(v) {
    var n = parseInt(String(v.title).replace(/[^0-9]/g, ''), 10);
    return isNaN(n) ? null : n;
  }

  // variants: [{title, ...}] -> {variant, length, diff} for the length nearest cm.
  // Ties go to the longer ski.
  function nearestVariant(variants, cm) {
    var best = null;
    (variants || []).forEach(function (v) {
      var len = variantLength(v);
      if (len === null) return;
      var diff = Math.abs(len - cm);
      if (!best || diff < best.diff || (diff === best.diff && len > best.length)) {
        best = { variant: v, length: len, diff: diff };
      }
    });
    return best;
  }

  // 'instock' | 'preorder' | 'soldout'
  function variantStatus(v) {
    if (!v.available) return 'soldout';
    if (v.inventory_management && Number(v.inventory_quantity) <= 0) return 'preorder';
    return 'instock';
  }

  function maxDev(target) { return Math.max(Math.abs(target - 1), Math.abs(target - 5)); }

  function maxPossiblePenalty(answers) {
    var S = CONFIG.scoring;
    var total = 0;
    var pw = S.park.weights[answers.park] || 0;
    total += pw * maxDev(S.park.targets[answers.park]);
    total += S.stiffness.weight * maxDev(S.stiffness.targets[answers.feel]);
    total += S.powder.weight * maxDev(S.powder.targets[answers.pow]);
    var tt = S.touring.targets[answers.tour];
    if (tt !== null && tt !== undefined) total += (S.touring.weights[answers.tour] || 0) * maxDev(tt);
    total += CONFIG.abilityMismatchPenalty + CONFIG.sizeFarPenalty;
    if (answers.level === 'expert') total += CONFIG.expertListsBeginnerPenalty;
    return total;
  }

  // Returns {score 0-100, penalty, parts:{park,stiffness,powder,touring,ability,size}}.
  // recCm is the recommended length for this ski (see recommendLength).
  function scoreSki(ski, answers, recCm) {
    var S = CONFIG.scoring;
    var parts = {};
    parts.park = (S.park.weights[answers.park] || 0) * Math.abs(S.park.targets[answers.park] - ski.scores.park);
    parts.stiffness = S.stiffness.weight * Math.abs(S.stiffness.targets[answers.feel] - ski.scores.stiffness);
    parts.powder = S.powder.weight * Math.abs(S.powder.targets[answers.pow] - ski.scores.powder);
    var tt = S.touring.targets[answers.tour];
    parts.touring = (tt === null || tt === undefined) ? 0 :
      (S.touring.weights[answers.tour] || 0) * Math.abs(tt - ski.scores.touring);

    var abilities = (ski.abilities || []).map(function (a) { return String(a).trim().toLowerCase(); });
    parts.ability = 0;
    if (abilities.indexOf(answers.level) === -1) parts.ability += CONFIG.abilityMismatchPenalty;
    if (answers.level === 'expert' && abilities.indexOf('beginner') !== -1) parts.ability += CONFIG.expertListsBeginnerPenalty;

    parts.size = 0;
    var near = nearestVariant(ski.variants, recCm);
    if (near && near.diff > CONFIG.sizeFarCm) parts.size = CONFIG.sizeFarPenalty;

    var penalty = parts.park + parts.stiffness + parts.powder + parts.touring + parts.ability + parts.size;
    var score = Math.round(100 - penalty * 100 / maxPossiblePenalty(answers));
    return { score: Math.max(0, Math.min(100, score)), penalty: penalty, parts: parts };
  }

  function totalInventory(ski) {
    return (ski.variants || []).reduce(function (n, v) { return n + Math.max(0, Number(v.inventory_quantity) || 0); }, 0);
  }

  // Keep only variants we're willing to show (pre-order hidden when the section says so).
  function visibleVariants(ski, showPreorder) {
    return (ski.variants || []).filter(function (v) {
      return showPreorder || variantStatus(v) !== 'preorder';
    });
  }

  function whyItFits(ski, answers, rec) {
    var S = CONFIG.scoring;
    var out = [];
    var waist = ski.waist ? ski.waist + ' mm' : null;
    var cands = [];

    var parkT = S.park.targets[answers.park];
    var parkD = Math.abs(parkT - ski.scores.park);
    cands.push({ d: parkD * (S.park.weights[answers.park] || 0), ok: parkD <= 1,
      text: parkT >= 3.5 ? 'Twin-style shape that\'s ready for park laps, side hits and switch landings'
        : 'Directional shape made for skiing the whole mountain, not just the park' });

    var stiffD = Math.abs(S.stiffness.targets[answers.feel] - ski.scores.stiffness);
    cands.push({ d: stiffD * S.stiffness.weight, ok: stiffD <= 1,
      text: answers.feel === 'powerful' ? 'Stiff, damp flex that stays stable at speed and charges through chop'
        : answers.feel === 'playful' ? 'Soft, poppy flex that\'s easy to bend, butter and throw around'
        : 'Balanced flex with a bit of snap and a bit of stability' });

    var powD = Math.abs(S.powder.targets[answers.pow] - ski.scores.powder);
    cands.push({ d: powD * S.powder.weight, ok: powD <= 1,
      text: answers.pow === 'every_chance' ? 'Built to float on storm days' + (waist ? ' with a ' + waist + ' waist' : '')
        : answers.pow === 'rarely' ? 'Grippy, quick-edging feel for groomers and hardpack'
        : (waist ? 'A ' + waist + ' waist' : 'Versatile width') + ' that handles mixed resort conditions' });

    var tt = S.touring.targets[answers.tour];
    if (tt !== null && tt !== undefined) {
      var tD = Math.abs(tt - ski.scores.touring);
      cands.push({ d: tD * (S.touring.weights[answers.tour] || 0), ok: tD <= 1,
        text: answers.tour === 'yes' ? 'Lighter build that makes the uphill a lot less work'
          : 'Light enough to take touring when you\'re ready' });
    }

    cands.sort(function (a, b) { return a.d - b.d; });
    cands.forEach(function (c) { if (c.ok && out.length < 2) out.push(c.text); });

    var abilities = (ski.abilities || []).map(function (a) { return String(a).trim().toLowerCase(); });
    if (out.length < 2 && abilities.indexOf(answers.level) !== -1) {
      out.push('Made for ' + answers.level + ' skiers');
    }
    if (rec) out.push('A ' + rec + ' cm ski matches your height and weight');
    return out.slice(0, 3);
  }

  // Rank skis for a set of answers. opts: {count, showPreorder}
  function rankSkis(skis, answers, opts) {
    opts = opts || {};
    var count = opts.count || CONFIG.resultsCount;
    var showPreorder = opts.showPreorder !== false;
    var profile = { heightCm: answers.heightCm, weightKg: answers.weightKg, level: answers.level, feel: answers.feel };

    var rows = [];
    skis.forEach(function (ski) {
      if (ski.touringOnly && answers.tour !== 'yes') return; // touring-only skis need "Yes, touring setup"
      var vars = visibleVariants(ski, showPreorder);
      if (!vars.length) return;
      var shown = Object.assign({}, ski, { variants: vars });
      var rec = recommendLength(profile, ski);
      var sc = scoreSki(shown, answers, rec);
      var near = nearestVariant(vars, rec);
      var purchasable = !!near && variantStatus(near.variant) !== 'soldout';
      rows.push({
        ski: shown, recommendedCm: rec, nearest: near, score: sc.score, parts: sc.parts,
        purchasable: purchasable, inventory: totalInventory(ski)
      });
    });

    rows.sort(function (a, b) {
      return (b.score - a.score) ||
        ((b.purchasable ? 1 : 0) - (a.purchasable ? 1 : 0)) ||
        (b.inventory - a.inventory);
    });

    var top = rows.slice(0, count);
    if (top.length === count && count > 0 && !top.some(function (r) { return r.purchasable; })) {
      var swap = rows.slice(count).filter(function (r) { return r.purchasable; })[0];
      if (swap) top[count - 1] = swap;
    }
    top.forEach(function (r) { r.why = whyItFits(r.ski, answers, r.recommendedCm); });
    return top;
  }

  /* ------------------------------------------------------------------
   * Units + hash helpers (pure)
   * ------------------------------------------------------------------ */
  function inToCm(inches) { return inches * CM_PER_IN; }
  function cmToIn(cm) { return cm / CM_PER_IN; }
  function lbToKg(lb) { return lb / LB_PER_KG; }
  function kgToLb(kg) { return kg * LB_PER_KG; }
  function formatFtIn(cm) {
    var total = Math.round(cmToIn(cm));
    return Math.floor(total / 12) + "'" + (total % 12) + '"';
  }

  function validateBody(heightCm, weightKg) {
    var L = CONFIG.limits;
    var errs = {};
    if (heightCm == null || !isFinite(heightCm)) {
      errs.height = 'Select your height.';
    } else if (heightCm < L.heightCm[0] || heightCm > L.heightCm[1]) {
      errs.height = 'Pick a height between ' + formatFtIn(L.heightCm[0]) + ' and ' + formatFtIn(L.heightCm[1]) + ' (' + L.heightCm[0] + '–' + L.heightCm[1] + ' cm).';
    }
    var lbs = weightKg == null ? NaN : kgToLb(weightKg);
    if (weightKg == null || !isFinite(weightKg)) {
      errs.weight = 'Enter your weight.';
    } else if (lbs < L.weightLbs[0] - 0.5 || lbs > L.weightLbs[1] + 0.5) {
      errs.weight = 'Enter a weight between ' + L.weightLbs[0] + ' and ' + L.weightLbs[1] + ' lbs (' +
        Math.round(lbToKg(L.weightLbs[0])) + '–' + Math.round(lbToKg(L.weightLbs[1])) + ' kg).';
    }
    return errs;
  }

  function encodeHash(a, units) {
    var p = [];
    Object.keys(HASH_KEYS).forEach(function (k) {
      if (a[k]) p.push(HASH_KEYS[k] + '=' + (k === 'level' ? LEVEL_CODES[a[k]] : a[k]));
    });
    if (a.heightCm) p.push('h=' + Math.round(a.heightCm));
    if (a.weightKg) p.push('w=' + Math.round(a.weightKg));
    if (units) p.push('u=' + (units === 'metric' ? 'm' : 'i'));
    return '#' + ANCHOR + (p.length ? '?' + p.join('&') : '');
  }

  function decodeHash(hash) {
    var out = { answers: {}, units: null };
    var m = String(hash || '').match(/^#find-your-planks\?(.*)$/);
    if (!m) return out;
    var params = {};
    m[1].split('&').forEach(function (kv) {
      var i = kv.indexOf('=');
      if (i > 0) params[decodeURIComponent(kv.slice(0, i))] = decodeURIComponent(kv.slice(i + 1));
    });
    QUESTIONS.forEach(function (q) {
      var raw = params[HASH_KEYS[q.key]];
      if (!raw) return;
      var val = raw;
      if (q.key === 'level') {
        Object.keys(LEVEL_CODES).forEach(function (l) { if (LEVEL_CODES[l] === raw) val = l; });
      }
      if (q.options.some(function (o) { return o.value === val; })) out.answers[q.key] = val;
    });
    var h = parseFloat(params.h), w = parseFloat(params.w);
    if (isFinite(h)) out.answers.heightCm = h;
    if (isFinite(w)) out.answers.weightKg = w;
    if (params.u === 'm') out.units = 'metric';
    if (params.u === 'i') out.units = 'imperial';
    return out;
  }

  function isComplete(a) {
    return QUESTIONS.every(function (q) { return !!a[q.key]; }) &&
      isFinite(a.heightCm) && isFinite(a.weightKg) &&
      Object.keys(validateBody(a.heightCm, a.weightKg)).length === 0;
  }

  var pure = {
    CONFIG: CONFIG, QUESTIONS: QUESTIONS, scoreSki: scoreSki, recommendLength: recommendLength,
    nearestVariant: nearestVariant, variantStatus: variantStatus, rankSkis: rankSkis,
    whyItFits: whyItFits, encodeHash: encodeHash, decodeHash: decodeHash, validateBody: validateBody,
    formatFtIn: formatFtIn, isComplete: isComplete, maxPossiblePenalty: maxPossiblePenalty
  };

  if (typeof document === 'undefined') return pure;

  /* ------------------------------------------------------------------
   * Browser layer
   * ------------------------------------------------------------------ */
  var doc = document;

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function reducedMotion() {
    return root.matchMedia && root.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function track(name, payload) {
    payload = payload || {};
    try {
      if (root.Shopify && root.Shopify.analytics && typeof root.Shopify.analytics.publish === 'function') {
        root.Shopify.analytics.publish(name, payload);
      }
    } catch (e) { /* analytics must never break the quiz */ }
    try {
      if (root.dataLayer && typeof root.dataLayer.push === 'function') {
        root.dataLayer.push(Object.assign({ event: name }, payload));
      }
    } catch (e) { /* ignore */ }
  }

  function money(cents, formatted) {
    if (formatted) return formatted;
    try {
      if (root.theme && root.theme.Currency) {
        return root.theme.Currency.formatMoney(cents, root.theme.settings && root.theme.settings.moneyFormat);
      }
    } catch (e) { /* fall through */ }
    return (cents / 100).toFixed(2);
  }

  function parseWaist(sizing) {
    var nums = String(sizing || '').match(/\d+(\.\d+)?/g);
    return nums && nums.length >= 2 ? Math.round(parseFloat(nums[1])) : null;
  }

  function normalizeSkis(data) {
    return (data.skis || []).map(function (s) {
      var sp = s.specs || {};
      return Object.assign({}, s, { waist: parseWaist(sp.sizing) });
    });
  }

  function radiusLabel(sp) {
    if (!sp || !sp.radius || sp.radius.value == null || sp.radius.value === '') return '';
    var unit = String(sp.radius.unit || '').toLowerCase();
    var short = unit.indexOf('in') === 0 ? 'in' : (unit.indexOf('m') === 0 ? 'm' : unit);
    return Number(sp.radius.value) + ' ' + short + ' radius';
  }

  function Finder(el) {
    this.el = el;
    this.id = el.dataset.sectionId;
    var dataEl = el.querySelector('[data-rsf-data]');
    var data = {};
    try { data = JSON.parse(dataEl.textContent); } catch (e) { data = { skis: [] }; }
    this.cfg = Object.assign({ resultsCount: CONFIG.resultsCount, showPreorder: true, units: 'imperial' }, data.config || {});
    this.skis = normalizeSkis(data);
    this.units = this.cfg.units === 'metric' ? 'metric' : 'imperial';
    this.answers = {};
    this.step = 0; // 0..4 questions, 5 body, 6 results
    this.stage = el.querySelector('[data-rsf-stage]');
    this.live = el.querySelector('[data-rsf-live]');
    this.markers = el.querySelectorAll('[data-rsf-marker]');
    this.stepText = el.querySelector('[data-rsf-steptext]');
    this.progress = el.querySelector('[data-rsf-progress]');
    this.advanceTimer = null;
    this.restoreFromHash();
    this.render(false);
  }

  Finder.prototype.restoreFromHash = function () {
    var dec = decodeHash(root.location.hash);
    if (dec.units) this.units = dec.units;
    this.answers = dec.answers;
    if (isComplete(this.answers)) { this.step = 6; return; }
    var first = QUESTIONS.findIndex(function (q) { return !dec.answers[q.key]; });
    this.step = first === -1 ? 5 : first;
  };

  Finder.prototype.writeHash = function () {
    var h = encodeHash(this.answers, this.units);
    try { root.history.replaceState(null, '', root.location.pathname + root.location.search + h); }
    catch (e) { /* ignore */ }
  };

  Finder.prototype.announce = function (msg) {
    if (!this.live) return;
    this.live.textContent = '';
    var live = this.live;
    root.setTimeout(function () { live.textContent = msg; }, 30);
  };

  Finder.prototype.setProgress = function () {
    var s = this.step;
    this.el.classList.toggle('is-results', s === 6);
    if (this.progress) this.progress.hidden = s === 6;
    this.markers.forEach(function (m, i) {
      m.classList.toggle('is-done', i < s);
      m.classList.toggle('is-current', i === s);
    });
    if (this.stepText) this.stepText.textContent = s === 6 ? '' : 'Step ' + (s + 1) + ' of 6';
  };

  Finder.prototype.render = function (userInitiated) {
    this.setProgress();
    if (this.step <= 4) this.renderQuestion(this.step);
    else if (this.step === 5) this.renderBody();
    else this.renderResults();
    if (userInitiated) this.afterStepChange();
  };

  Finder.prototype.afterStepChange = function () {
    var self = this;
    if (this.step === 6) {
      this.announce('Your results are ready.');
    } else {
      var t = this.step <= 4 ? QUESTIONS[this.step].title : 'Dial in your length';
      this.announce('Step ' + (this.step + 1) + ' of 6: ' + t);
    }
    // keep the user oriented, especially on phones
    var rect = this.el.getBoundingClientRect();
    if (root.innerWidth < 769 && (rect.top < 0 || rect.top > root.innerHeight * 0.6)) {
      this.scrollIntoView();
    }
    this.focusStep();
    track('rmu_ski_finder_step', { step: this.step + 1, answers: Object.assign({}, this.answers) });
  };

  Finder.prototype.scrollIntoView = function () {
    this.el.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' });
  };

  Finder.prototype.focusStep = function () {
    var target;
    if (this.step <= 4) {
      target = this.stage.querySelector('input[type=radio]:checked') || this.stage.querySelector('input[type=radio]');
    } else if (this.step === 5) {
      target = this.stage.querySelector('select, input');
    } else {
      target = this.stage.querySelector('[data-rsf-results-heading]');
    }
    if (target) target.focus({ preventScroll: true });
  };

  Finder.prototype.renderQuestion = function (idx) {
    var q = QUESTIONS[idx];
    var self = this;
    var name = this.id + '-' + q.key;
    var cur = this.answers[q.key];
    var html = '<fieldset class="rsf__fieldset"><legend class="rsf__legend h3">' + esc(q.title) + '</legend><div class="rsf__options">';
    q.options.forEach(function (o, i) {
      var id = name + '-' + i;
      html += '<div class="rsf__option">' +
        '<input class="rsf__radio" type="radio" name="' + name + '" id="' + id + '" value="' + esc(o.value) + '"' + (cur === o.value ? ' checked' : '') + '>' +
        '<label class="rsf__card" for="' + id + '"><span class="rsf__card-label">' + esc(o.label) + '</span>' +
        '<span class="rsf__card-desc">' + esc(o.desc) + '</span></label></div>';
    });
    html += '</div></fieldset>' + this.navHtml(idx > 0, cur ? 'next' : null);
    this.stage.innerHTML = html;

    var radios = this.stage.querySelectorAll('.rsf__radio');
    radios.forEach(function (r) {
      r.addEventListener('change', function () {
        self.answers[q.key] = r.value;
        self.writeHash();
        var nextBtn = self.stage.querySelector('[data-rsf-next]');
        if (nextBtn) nextBtn.hidden = false;
      });
    });
    // Pointer / touch picks auto-advance; keyboard users confirm with Enter or Next.
    this.stage.querySelectorAll('.rsf__card').forEach(function (card) {
      card.addEventListener('click', function (e) {
        if (e.detail === 0) return; // synthetic click from keyboard
        var input = doc.getElementById(card.getAttribute('for'));
        self.answers[q.key] = input.value;
        self.writeHash();
        root.clearTimeout(self.advanceTimer);
        self.advanceTimer = root.setTimeout(function () { self.go(self.step + 1); }, reducedMotion() ? 0 : CONFIG.autoAdvanceMs);
      });
    });
    radios.forEach(function (r) {
      r.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' && r.checked) { e.preventDefault(); self.go(self.step + 1); }
      });
    });
    this.bindNav();
  };

  Finder.prototype.navHtml = function (showBack, nextMode, primaryLabel) {
    var h = '<div class="rsf__nav">';
    h += showBack ? '<button type="button" class="btn btn--secondary btn--small rsf__back" data-rsf-back>Back</button>' : '<span></span>';
    if (primaryLabel) h += '<button type="submit" class="btn rsf__primary">' + esc(primaryLabel) + '</button>';
    else if (nextMode) h += '<button type="button" class="btn btn--small rsf__next" data-rsf-next>Next</button>';
    else h += '<button type="button" class="btn btn--small rsf__next" data-rsf-next hidden>Next</button>';
    return h + '</div>';
  };

  Finder.prototype.bindNav = function () {
    var self = this;
    var back = this.stage.querySelector('[data-rsf-back]');
    var next = this.stage.querySelector('[data-rsf-next]');
    if (back) back.addEventListener('click', function () { self.go(self.step - 1); });
    if (next) next.addEventListener('click', function () { self.go(self.step + 1); });
  };

  Finder.prototype.go = function (step) {
    root.clearTimeout(this.advanceTimer);
    this.step = Math.max(0, Math.min(6, step));
    this.render(true);
  };

  Finder.prototype.renderBody = function () {
    var self = this;
    var a = this.answers;
    var metric = this.units === 'metric';
    var id = this.id;

    var heightOpts = '<option value="">Select height</option>';
    var selCm = a.heightCm ? Math.round(a.heightCm) : null;
    if (metric) {
      for (var c = CONFIG.limits.heightCm[0]; c <= CONFIG.limits.heightCm[1]; c++) {
        heightOpts += '<option value="' + c + '"' + (selCm === c ? ' selected' : '') + '>' + c + ' cm</option>';
      }
    } else {
      var selIn = a.heightCm ? Math.round(cmToIn(a.heightCm)) : null;
      for (var i = CONFIG.heightIn[0]; i <= CONFIG.heightIn[1]; i++) {
        heightOpts += '<option value="' + i + '"' + (selIn === i ? ' selected' : '') + '>' + Math.floor(i / 12) + "'" + (i % 12) + '"</option>';
      }
    }
    var wVal = '';
    if (a.weightKg) wVal = metric ? Math.round(a.weightKg) : Math.round(kgToLb(a.weightKg));

    var html = '<form class="rsf__form" novalidate>' +
      '<fieldset class="rsf__fieldset"><legend class="rsf__legend h3">Dial in your length</legend>' +
      '<p class="rsf__help">Height and weight help us recommend the right length for each ski.</p>' +
      '<div class="rsf__body">' +
      '<div class="rsf__field"><div class="rsf__field-head"><label for="' + id + '-height">Height</label>' +
      this.unitToggleHtml('height', metric ? 'cm' : 'ft', ['ft', 'cm']) + '</div>' +
      '<select id="' + id + '-height" class="rsf__input" required aria-describedby="' + id + '-height-err">' + heightOpts + '</select>' +
      '<p class="rsf__error" id="' + id + '-height-err" role="alert" hidden></p></div>' +
      '<div class="rsf__field"><div class="rsf__field-head"><label for="' + id + '-weight">Weight</label>' +
      this.unitToggleHtml('weight', metric ? 'kg' : 'lbs', ['lbs', 'kg']) + '</div>' +
      '<input id="' + id + '-weight" class="rsf__input" type="number" inputmode="numeric" min="1" step="1" value="' + wVal + '" placeholder="' + (metric ? 'kg' : 'lbs') + '" required aria-describedby="' + id + '-weight-err">' +
      '<p class="rsf__error" id="' + id + '-weight-err" role="alert" hidden></p></div>' +
      '</div></fieldset>' + this.navHtml(true, null, 'Find My Skis') + '</form>';
    this.stage.innerHTML = html;

    this.bindNav();
    var form = this.stage.querySelector('form');
    form.addEventListener('submit', function (e) { e.preventDefault(); self.submitBody(); });
    this.stage.querySelectorAll('[data-rsf-unit]').forEach(function (btn) {
      btn.addEventListener('click', function () { self.switchUnit(btn.dataset.rsfUnit); });
    });
  };

  Finder.prototype.unitToggleHtml = function (kind, active, units) {
    var h = '<div class="rsf__units" role="group" aria-label="' + (kind === 'height' ? 'Height' : 'Weight') + ' units">';
    units.forEach(function (u) {
      h += '<button type="button" class="rsf__unit' + (u === active ? ' is-active' : '') + '" data-rsf-unit="' + u + '" aria-pressed="' + (u === active) + '">' + u + '</button>';
    });
    return h + '</div>';
  };

  // Reads the current inputs, converts, and re-renders in the other unit system.
  Finder.prototype.switchUnit = function (unit) {
    var metric = (unit === 'cm' || unit === 'kg');
    var next = metric ? 'metric' : 'imperial';
    if (next === this.units) return;
    this.captureBody();
    this.units = next;
    this.writeHash();
    this.renderBody();
    var sel = this.stage.querySelector('[data-rsf-unit="' + unit + '"]');
    if (sel) sel.focus({ preventScroll: true });
  };

  Finder.prototype.captureBody = function () {
    var h = this.stage.querySelector('select');
    var w = this.stage.querySelector('input[type=number]');
    if (!h || !w) return;
    var hv = parseFloat(h.value), wv = parseFloat(w.value);
    if (isFinite(hv)) this.answers.heightCm = this.units === 'metric' ? hv : inToCm(hv);
    if (isFinite(wv)) this.answers.weightKg = this.units === 'metric' ? wv : lbToKg(wv);
  };

  Finder.prototype.submitBody = function () {
    this.captureBody();
    var errs = validateBody(this.answers.heightCm, this.answers.weightKg);
    var he = this.stage.querySelector('#' + this.id + '-height-err');
    var we = this.stage.querySelector('#' + this.id + '-weight-err');
    he.hidden = !errs.height; he.textContent = errs.height || '';
    we.hidden = !errs.weight; we.textContent = errs.weight || '';
    var hs = this.stage.querySelector('select'), wi = this.stage.querySelector('input[type=number]');
    hs.setAttribute('aria-invalid', errs.height ? 'true' : 'false');
    wi.setAttribute('aria-invalid', errs.weight ? 'true' : 'false');
    if (errs.height) { hs.focus(); return; }
    if (errs.weight) { wi.focus(); return; }
    this.answers.heightCm = Math.round(this.answers.heightCm);
    this.answers.weightKg = Math.round(this.answers.weightKg * 10) / 10;
    this.writeHash();
    this.go(6);
  };

  Finder.prototype.recapText = function () {
    var a = this.answers;
    var body = this.units === 'metric'
      ? Math.round(a.heightCm) + ' cm, ' + Math.round(a.weightKg) + ' kg'
      : formatFtIn(a.heightCm) + ', ' + Math.round(kgToLb(a.weightKg)) + ' lb';
    return [RECAP.level[a.level], RECAP.feel[a.feel], RECAP.pow[a.pow], body].join(' · ');
  };

  Finder.prototype.renderResults = function () {
    var self = this;
    var a = this.answers;
    if (!isComplete(a)) { this.step = 0; this.render(false); return; }
    var results = rankSkis(this.skis, a, { count: this.cfg.resultsCount, showPreorder: this.cfg.showPreorder });
    this.results = results;

    var html = '<div class="rsf__results">' +
      '<div class="rsf__results-head"><h3 class="h2 rsf__results-title" tabindex="-1" data-rsf-results-heading>Your Skis</h3>' +
      '<p class="rsf__intro">Here are your best matches. Questions about fit? Reach out and we\'ll help you dial it in.</p>' +
      '<p class="rsf__recap">' + esc(this.recapText()) + '</p>' +
      '<button type="button" class="rsf__link" data-rsf-edit>Edit answers</button></div>';

    if (!results.length) {
      html += '<p>We couldn\'t find a match right now. Browse all 2027 skis above, or reach out and we\'ll help you pick.</p>';
    } else {
      html += '<div class="rsf__grid" style="--rsf-cols:' + Math.min(Math.max(results.length, 1), 3) + '">';
      results.forEach(function (r, i) { html += self.cardHtml(r, i); });
      html += '</div>';
    }
    html += '<div class="rsf__results-foot"><button type="button" class="btn btn--secondary" data-rsf-restart>Start over</button></div></div>';
    this.stage.innerHTML = html;

    this.stage.querySelector('[data-rsf-edit]').addEventListener('click', function () { self.go(0); });
    this.stage.querySelector('[data-rsf-restart]').addEventListener('click', function () {
      self.answers = {};
      self.writeHash();
      self.go(0);
    });
    this.stage.querySelectorAll('[data-rsf-card]').forEach(function (card) { self.bindCard(card, results[Number(card.dataset.rsfCard)]); });

    if (!this.completeTracked || this.completeTracked !== this.recapText()) {
      this.completeTracked = this.recapText();
      track('rmu_ski_finder_complete', {
        answers: Object.assign({}, a),
        top_skis: results.map(function (r) { return r.ski.handle; })
      });
    }
  };

  Finder.prototype.cardHtml = function (r, i) {
    var s = r.ski;
    var uid = this.id + '-r' + i;
    var near = r.nearest;
    var chips = [];
    if (s.waist) chips.push(s.waist + ' mm waist');
    var rad = radiusLabel(s.specs);
    if (rad) chips.push(rad);

    var onSale = s.compareAtPrice && s.compareAtPrice > s.price;
    var priceHtml = '<span class="grid-product__price' + (onSale ? ' grid-product__price--sale' : '') + '">' +
      (onSale ? '<span class="grid-product__price--original">' + esc(money(s.compareAtPrice, s.compareFormatted)) + '</span>' : '') +
      esc(money(s.price, s.priceFormatted)) + '</span>';

    var sizes = '<fieldset class="rsf__sizes"><legend class="rsf__sizes-legend">Size</legend><div class="rsf__size-row">';
    var rec = near ? near.length : null;
    s.variants.forEach(function (v) {
      var len = variantLength(v);
      var st = variantStatus(v);
      var checked = near && v.id === near.variant.id;
      var stLabel = st === 'instock' ? 'In stock' : st === 'preorder' ? 'Pre-order' : 'Sold out';
      var sid = uid + '-v' + v.id;
      sizes += '<div class="rsf__size"><input class="rsf__radio" type="radio" name="' + uid + '-size" id="' + sid + '" value="' + v.id + '" data-status="' + st + '"' +
        (checked ? ' checked' : '') + (st === 'soldout' ? ' disabled' : '') + '>' +
        '<label class="rsf__size-label" for="' + sid + '"><span class="rsf__size-len">' + (len ? len + ' cm' : esc(v.title)) + '</span>' +
        '<span class="rsf__size-state rsf__size-state--' + st + '">' + stLabel + '</span></label></div>';
    });
    sizes += '</div></fieldset>';

    var fit = '<p class="rsf__rec"><strong>Recommended: ' + r.recommendedCm + ' cm</strong>';
    if (near && near.diff > CONFIG.length.showFitNoteOverCm) {
      fit += '<span class="rsf__fitnote">Closest RMU size is ' + near.length + ' cm — reach out and we\'ll help with fit.</span>';
    }
    fit += '</p>';

    var why = '<div class="rsf__why"><p class="rsf__why-title">Why it fits you</p><ul>' +
      r.why.map(function (w) { return '<li>' + esc(w) + '</li>'; }).join('') + '</ul></div>';

    var bindings = this.answers.tour === 'yes'
      ? '<p class="rsf__bindings"><a href="' + CONFIG.bindingsUrl + '">Bindings &amp; Accessories</a></p>' : '';

    var meta = chips.map(function (c) { return '<span>' + esc(c) + '</span>'; }).join('') + priceHtml;

    return '<article class="rsf__result" data-rsf-card="' + i + '">' +
      '<p class="rsf__kicker">' + (i === 0 ? 'Best match' : 'Match #' + (i + 1)) + ' &middot; ' + r.score + '%</p>' +
      '<h4 class="rsf__result-title"><a href="' + esc(s.url) + '">' + esc(s.title) + '</a></h4>' +
      '<p class="rsf__meta">' + meta + '</p>' +
      '<a class="rsf__result-img" href="' + esc(s.url) + '" tabindex="-1" aria-hidden="true">' +
      (s.image ? '<img src="' + esc(s.image) + '" alt="' + esc(s.imageAlt || s.title) + '" loading="lazy" width="600">' : '') + '</a>' +
      '<div class="rsf__result-body">' +
      (s.pitch ? '<p class="rsf__pitch">' + esc(s.pitch) + '</p>' : '') + why + fit + sizes +
      '<form class="rsf__buy" data-rsf-form action="/cart/add" method="post"><input type="hidden" name="id" value="' + (near ? near.variant.id : '') + '">' +
      '<button type="submit" class="btn btn--full rsf__atc" data-rsf-atc></button>' +
      '<a class="btn btn--secondary btn--full" href="' + esc(s.url) + '">View ski</a>' +
      '<p class="rsf__msg" role="alert" data-rsf-msg hidden></p></form>' + bindings + '</div></article>';
  };

  Finder.prototype.bindCard = function (card, r) {
    var self = this;
    var form = card.querySelector('[data-rsf-form]');
    var idInput = form.querySelector('input[name=id]');
    var btn = card.querySelector('[data-rsf-atc]');
    var msg = card.querySelector('[data-rsf-msg]');
    var radios = card.querySelectorAll('.rsf__size input');

    function sync() {
      var chosen = card.querySelector('.rsf__size input:checked');
      var label = btn;
      if (!chosen) { btn.disabled = true; btn.textContent = 'Sold out'; idInput.value = ''; return; }
      idInput.value = chosen.value;
      var len = chosen.nextElementSibling.querySelector('.rsf__size-len').textContent;
      btn.disabled = false;
      label.textContent = (chosen.dataset.status === 'preorder' ? 'Pre-order ' : 'Add ') + len + (chosen.dataset.status === 'preorder' ? '' : ' to cart');
    }
    radios.forEach(function (rd) { rd.addEventListener('change', sync); });
    sync();

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (!idInput.value || btn.classList.contains('btn--loading')) return;
      msg.hidden = true;
      btn.classList.add('btn--loading');
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
        body: JSON.stringify({ id: Number(idInput.value), quantity: 1 })
      }).then(function (res) {
        return res.json().then(function (body) { return { ok: res.ok, body: body }; });
      }).then(function (out) {
        btn.classList.remove('btn--loading');
        if (!out.ok) {
          msg.textContent = out.body.description || out.body.message || 'Sorry, we couldn\'t add that to your cart.';
          msg.hidden = false;
          return;
        }
        track('rmu_ski_finder_add_to_cart', { handle: r.ski.handle, variant_id: Number(idInput.value), answers: Object.assign({}, self.answers) });
        if (root.theme && root.theme.settings && root.theme.settings.cartType === 'page') {
          root.location.href = (root.theme.routes && root.theme.routes.cartPage) || '/cart';
          return;
        }
        // Same event Impulse's own product forms fire; the cart drawer listens for it.
        form.dispatchEvent(new CustomEvent('ajaxProduct:added', {
          detail: { product: out.body, addToCartBtn: btn }, bubbles: true
        }));
      }).catch(function () {
        btn.classList.remove('btn--loading');
        msg.textContent = 'Sorry, something went wrong adding that to your cart. Please try again.';
        msg.hidden = false;
      });
    });
  };

  /* ---- #find-your-planks deep link ---- */
  function hashTargets() { return root.location.hash.indexOf('#' + ANCHOR) === 0; }

  function scrollToQuiz(finder) {
    var el = finder.el;
    var offset = 0;
    var header = doc.querySelector('.site-header');
    var rootStyle = root.getComputedStyle(doc.documentElement);
    var hh = parseFloat(rootStyle.getPropertyValue('--header-height')) || (header ? header.offsetHeight : 0);
    var ab = parseFloat(rootStyle.getPropertyValue('--sticky-announcement-bar-height')) || 0;
    offset = hh + ab + 12;
    function targetTop() { return Math.max(0, el.getBoundingClientRect().top + root.pageYOffset - offset); }
    root.scrollTo({ top: targetTop(), behavior: reducedMotion() ? 'auto' : 'smooth' });

    // Lazy-loaded product images above the quiz can shift the page while we scroll;
    // re-aim a few times until we land on it, then focus the first question.
    var tries = 0;
    (function settle() {
      root.setTimeout(function () {
        tries += 1;
        if (Math.abs(root.pageYOffset - targetTop()) > 12 && tries < 6) {
          root.scrollTo({ top: targetTop(), behavior: 'auto' });
          settle();
        } else {
          finder.focusStep();
        }
      }, 450);
    })();
  }

  var instances = [];

  function init(scope) {
    (scope || doc).querySelectorAll('[data-rmu-ski-finder]').forEach(function (el) {
      if (el.__rsf) return;
      el.__rsf = new Finder(el);
      instances.push(el.__rsf);
    });
  }

  function handleHash() {
    if (!hashTargets() || !instances.length) return;
    scrollToQuiz(instances[0]);
  }

  function boot() {
    init();
    if (hashTargets()) root.setTimeout(handleHash, 60);
  }

  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', boot);
  else boot();

  root.addEventListener('hashchange', function () {
    // Announcement-bar link while already on the page: scroll + focus. Answers only
    // change when the hash itself carries them (a pasted/shared results link).
    if (!hashTargets() || !instances.length) return;
    if (root.location.hash.indexOf('?') !== -1) {
      instances[0].restoreFromHash();
      instances[0].render(false);
    }
    handleHash();
  });

  // "Not sure which ski?" link near the collection title: smooth-scroll instead of a hard jump.
  doc.addEventListener('click', function (e) {
    var a = e.target.closest && e.target.closest('a[data-rsf-jump]');
    if (!a || !instances.length) return;
    e.preventDefault();
    if (!hashTargets()) {
      try { root.history.pushState(null, '', root.location.pathname + root.location.search + '#' + ANCHOR); } catch (err) { /* ignore */ }
    }
    scrollToQuiz(instances[0]);
  });

  doc.addEventListener('shopify:section:load', function (e) { init(e.target); });

  pure.init = init;
  return pure;
});
